// ============================================================================
// COMPONENTE: PROFITABILITY WIDGET (JURIMETRIA INTERNA V1.0)
// OBJETIVO: Cruzar Financeiro vs Timesheet para achar o "Valor Hora Real".
// ============================================================================
import React, { useState, useEffect, useMemo } from 'react';
import { fetchFinancialLedger, fetchTimesheetEntries } from '../services/FinanceRepository';

import { FIREBASE_CONFIG } from '../config/Config';

const appId = FIREBASE_CONFIG.projectId;

const formatCurrency = (val) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

const ProfitabilityWidget = () => {
    const [loading, setLoading] = useState(true);
    const [financialData, setFinancialData] = useState([]);
    const [timesheetData, setTimesheetData] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                // 1. Busca Receitas (Últimos 1000 lançamentos para ter amostra relevante)
                const finData = await fetchFinancialLedger(1000);
                
                // 2. Busca Horas (Últimos 1000 registros)
                const timeData = await fetchTimesheetEntries(1000);

                setFinancialData(finData);
                setTimesheetData(timeData);
            } catch (e) {
                console.error("Erro no BI:", e);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, []);

    // CÉREBRO DO WIDGET: O CÁLCULO
    const metrics = useMemo(() => {
        const clientStats = {};

        // A. Soma Receitas por Cliente
        financialData.forEach(item => {
            if (item.movementType === 'CREDIT' && item.linkedName && item.linkedToType === 'CLIENT') {
                const name = item.linkedName;
                if (!clientStats[name]) clientStats[name] = { revenue: 0, hours: 0 };
                clientStats[name].revenue += Number(item.value);
            }
        });

        // B. Soma Horas por Cliente
        timesheetData.forEach(item => {
            if (item.clientName) {
                // Tenta achar match aproximado de nome ou exato
                const name = Object.keys(clientStats).find(k => k.includes(item.clientName) || item.clientName.includes(k)) || item.clientName;
                
                if (!clientStats[name]) clientStats[name] = { revenue: 0, hours: 0 };
                // Converte segundos para horas decimais
                clientStats[name].hours += (item.seconds / 3600);
            }
        });

        // C. Calcula Rentabilidade (R$/h)
        const ranking = Object.entries(clientStats)
            .map(([name, data]) => ({
                name,
                revenue: data.revenue,
                hours: data.hours,
                // Evita divisão por zero
                hourlyRate: data.hours > 0.1 ? (data.revenue / data.hours) : 0
            }))
            .filter(c => c.revenue > 0 || c.hours > 0) // Remove vazios
            .sort((a, b) => b.hourlyRate - a.hourlyRate); // Do maior para o menor

        return {
            top: ranking.slice(0, 3), // Top 3 Mais Rentáveis
            bottom: ranking.filter(r => r.hours > 5 && r.hourlyRate < 200).slice(-3).reverse() // Top 3 "Prejuízo" (Muitas horas, pouco ganho)
        };
    }, [financialData, timesheetData]);

    if (loading) return <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 h-full flex items-center justify-center"><i className="fas fa-circle-notch fa-spin text-gray-300"></i></div>;

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 h-full flex flex-col relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-5">
                <i className="fas fa-brain text-6xl text-purple-900"></i>
            </div>
            
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <i className="fas fa-chart-network text-purple-600"></i> Inteligência de Rentabilidade
            </h3>

            <div className="flex-1 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
                {/* CAMPEÕES */}
                <div>
                    <p className="text-[10px] font-bold text-green-700 uppercase mb-2 flex items-center gap-1">
                        <i className="fas fa-trophy"></i> Clientes + Rentáveis (R$/h)
                    </p>
                    <div className="space-y-2">
                        {metrics.top.length === 0 ? <p className="text-xs text-gray-400 italic">Sem dados suficientes.</p> : metrics.top.map((c, i) => (
                            <div key={i} className="flex justify-between items-center bg-green-50/50 p-2 rounded border border-green-100">
                                <div className="truncate flex-1 pr-2">
                                    <p className="text-xs font-bold text-gray-700 truncate">{c.name}</p>
                                    <p className="text-[9px] text-gray-500">{c.hours.toFixed(1)}h trabalhadas</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-bold text-green-700">{formatCurrency(c.hourlyRate)}/h</p>
                                    <p className="text-[9px] text-green-600">Total: {formatCurrency(c.revenue)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ALERTA DE PREJUÍZO */}
                {metrics.bottom.length > 0 && (
                    <div className="border-t border-gray-100 pt-3">
                        <p className="text-[10px] font-bold text-red-600 uppercase mb-2 flex items-center gap-1 animate-pulse">
                            <i className="fas fa-exclamation-triangle"></i> Atenção: Baixa Rentabilidade
                        </p>
                        <div className="space-y-2">
                            {metrics.bottom.map((c, i) => (
                                <div key={i} className="flex justify-between items-center bg-red-50/50 p-2 rounded border border-red-100">
                                    <div className="truncate flex-1 pr-2">
                                        <p className="text-xs font-bold text-gray-700 truncate">{c.name}</p>
                                        <p className="text-[9px] text-red-400 font-bold">{c.hours.toFixed(1)}h consumidas!</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs font-bold text-red-600">{formatCurrency(c.hourlyRate)}/h</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProfitabilityWidget;
